


















































































import { getUserData } from "@/auth/getUserData";
import useImageGalleryModal from "@/composables/v3/useImageGalleryModal";
import useReadChat from "@/composables/v3/useReadChat";
// @ts-ignore
import i18n from "@/libs/i18n";
import { useWebsockets } from "@/libs/websockets";
import { useChatsStore } from "@/store/useChatsStore";
import { useContactsStore } from "@/store/useContactsStore";
import { useDownloadAttachmentsStore } from "@/store/useDownloadAttachmentsStore";
import { useUsersStore } from "@/store/useUsersStore";
import { decrypt } from "@/utils/crypto";
import { resolveUserStatus } from "@/utils/users";
import ChatLogEntry from "@/views/modules/messaging-inbox/chat-log/components/chatLogEntry.vue";
import ChatLogScrollToBottom from "@/views/modules/messaging-inbox/chat-log/components/chatLogScrollToBottom.vue";
import ImageGalleryModal from "@/views/modules/messaging-inbox/chat-log/components/imageGalleryModal.vue";
import {
    chatLogIsCurrentlyAtBottom,
    chatLogPs,
    EntryGroup,
    previousScrollHeightMinusScrollTop,
} from "@/views/modules/messaging-inbox/useChat";
import {
    BaseWebsocketEvent,
    EntryDirection,
    EntryDtoType,
    WebsocketChatUpdateEvent,
    WebsocketEntryUpdateEvent,
    WebsocketEvents,
    WebsocketInternalCommentEvent,
    WebsocketNewEntriesEvent,
} from "@saysimple-platform/dtos";
import { computed, defineComponent, nextTick, onUnmounted, ref } from "@vue/composition-api";
import { useIntersectionObserver } from "@vueuse/core";
import { BAvatar, BSpinner } from "bootstrap-vue";
import { DateTime } from "luxon";
import { Vue } from "vue/types/vue";
import { styleByColor } from "@/utils/color";

export default defineComponent({
    methods: { styleByColor },
    components: {
        BAvatar,
        BSpinner,
        ChatLogEntry,
        ChatLogScrollToBottom,
        ImageGalleryModal,
    },
    emits: ["scroll-to-bottom"],
    sockets: {
        // @see https://justinternet.atlassian.net/wiki/spaces/SAYS/pages/647004161/Frontend+events
        socketEvent(
            // @note it looks like the toast is not registered yet as plugin (unexpectedly), so instead of this.$toast we use this.toast
            this: Vue,
            socketMessage: { event: string; iv: string }
        ) {
            const chatsStore = useChatsStore();
            if (!chatsStore.activeChat) {
                return;
            }

            const wsMessage: BaseWebsocketEvent<keyof WebsocketEvents> = decrypt(socketMessage.event, socketMessage.iv);

            // @NOTICE all those websocket event should only do anything with the chats list.
            let payload, entry;
            const { read } = useReadChat();
            switch (wsMessage.type) {
                case "NEW_ENTRY":
                    payload = wsMessage.payload as WebsocketNewEntriesEvent;

                    if (payload.chat.id !== chatsStore.activeChat?.chat.id) {
                        return;
                    }

                    // Directly set the read-status to read if the incoming message belongs to the current active chat.
                    read(chatsStore.activeChat.chat.id, { showToast: false });

                    // We first remove the same message entry from the chat log first before adding it again. This way the order stays correct.
                    if (chatsStore.activeChat.entries.length > 0) {
                        payload.entries.forEach((wsEntry) => {
                            const chatLogEntryIndex = chatsStore.activeChat.entries.findIndex(
                                (activeChatEntry) => activeChatEntry.id === wsEntry.id
                            );

                            if (chatLogEntryIndex !== -1) {
                                chatsStore.activeChat.entries.splice(chatLogEntryIndex, 1);
                            }
                        });
                    }

                    // In the new situation, the backend can possibly give us multiple entries.
                    payload.entries.forEach((entry) => chatsStore.activeChat.entries.push(entry)); // ### "Add it to the active chat log"

                    if (chatLogIsCurrentlyAtBottom.value) {
                        nextTick(() => this.$emit("scroll-to-bottom"));
                    }

                    break;
                case "INTERNAL_COMMENT_CREATED":
                case "INTERNAL_COMMENT_UPDATED":
                case "INTERNAL_COMMENT_DELETED":
                    payload = wsMessage.payload as WebsocketInternalCommentEvent; // This type equals InformationEntryDto

                    if (payload.chatId !== chatsStore.activeChat?.chat.id) {
                        return;
                    }

                    chatsStore.activeChat.entries.push(payload);

                    if (chatLogIsCurrentlyAtBottom.value) {
                        nextTick(() => this.$emit("scroll-to-bottom"));
                    }

                    break;
                case "CHAT_UPDATE":
                    // @note when receiving this event, the active chat is possibly already be changed, so we don't know what the status was before...
                    payload = wsMessage.payload as WebsocketChatUpdateEvent;

                    if (payload.chat.id !== chatsStore.activeChat?.chat.id) {
                        return;
                    }

                    // If assigned to me, we dont need to do anything. This is to prevent closing the chat when you for example click on "assign to me".
                    // This is only true for non-archived (closed) chats.
                    if (payload.chat.assignedUserId === getUserData().id && payload.chat.status === "open") {
                        return;
                    }

                    if (this.$socket.client.id !== wsMessage.senderId) {
                        chatsStore.closeChat();
                    } else if (chatLogIsCurrentlyAtBottom.value) {
                        nextTick(() => this.$emit("scroll-to-bottom"));
                    }

                    break;
                case "ENTRY_UPDATE":
                    // @ts-ignore
                    entry = wsMessage.payload.entry as WebsocketEntryUpdateEvent;

                    if (chatsStore.activeChat?.chat.id !== entry.chatId) {
                        return;
                    }

                    chatsStore.updateEntry(entry);

                    break;
            }
        },
    },
    setup() {
        const chatsStore = useChatsStore();
        const usersStore = useUsersStore();
        const contactsStore = useContactsStore();

        const sentinel = ref(null);
        const sentinelIsVisible = ref(false);

        onUnmounted(() => {
            const { clearDownloads } = useDownloadAttachmentsStore();
            clearDownloads();
        });

        const loadMoreEntries = async () => {
            if (!chatLogPs.value) {
                return;
            }

            previousScrollHeightMinusScrollTop.value =
                chatLogPs.value.$el.scrollHeight - chatLogPs.value?.$el.scrollTop;

            await chatsStore.loadMoreEntries();

            chatLogPs.value.$el.scrollTop =
                chatLogPs.value?.$el.scrollHeight - previousScrollHeightMinusScrollTop.value;
        };

        useIntersectionObserver(
            sentinel,
            async ([{ isIntersecting }]) => {
                sentinelIsVisible.value = isIntersecting;

                if (isIntersecting) {
                    loadMoreEntries();
                }
            },
            {
                root: chatLogPs.value,
                rootMargin: "0px",
                threshold: 1.0,
            }
        );

        const resolveCreatedBy = ({
            createdById,
            createdByEntity,
        }: EntryDtoType): Pick<EntryGroup, "createdBy" | "createdByEntity"> => {
            switch (createdByEntity) {
                case "user":
                    return {
                        createdBy: usersStore.getUserById(createdById),
                        createdByEntity,
                    };
                case "contact":
                    return {
                        createdBy: contactsStore.activeChatContact,
                        createdByEntity,
                    };
                case "system":
                case "api":
                    return {
                        createdByEntity,
                    };
            }
        };

        const chatLog = computed(() => {
            const _entryGroups: EntryGroup[] = [];

            if (chatsStore.activeChat.chat) {
                const entries: EntryDtoType[] = chatsStore.activeChat.entries;

                if (entries.length === 0) {
                    return [];
                }

                //--------------------------------------------------------------

                let direction = entries[0].direction;
                let createdById = entries[0].createdById;

                let entryGroup: EntryGroup = {
                    ...resolveCreatedBy(entries[0]),
                    direction,
                    createdById,
                    entries: [],
                    createdAt: DateTime.now(),
                };

                //--------------------------------------------------------------

                let createdAt;
                // If-else for readability.
                if (entries[0].createdAt) {
                    createdAt = DateTime.fromISO(
                        DateTime.fromISO(entries[0].createdAt as unknown as string).toISODate()
                    );
                } else {
                    createdAt = DateTime.now();
                }

                let dateGroup: EntryGroup = {
                    createdByEntity: "system",
                    createdAt,
                    entries: [
                        {
                            time: createdAt,
                            type: "date",
                        },
                    ],
                };
                _entryGroups.push(dateGroup);

                //--------------------------------------------------------------

                entries.forEach((entry, index) => {
                    const entryCreatedAt = DateTime.fromISO(
                        DateTime.fromISO(entry.createdAt as unknown as string).toISODate()
                    );
                    const entryDiff = entryCreatedAt.diff(dateGroup.createdAt, ["years", "months", "days"]).days;

                    if (direction === entry.direction && createdById === entry.createdById && entryDiff <= 0) {
                        entryGroup.entries.push(entry);
                    } else {
                        direction = entry.direction as EntryDirection;
                        createdById = entry.createdById;

                        _entryGroups.push(entryGroup);
                        entryGroup = {
                            ...resolveCreatedBy(entry),
                            createdAt: entry.createdAt as unknown as DateTime,
                            direction,
                            createdById,
                            entries: [entry],
                        };

                        if (entryDiff > 0) {
                            _entryGroups.push({
                                createdByEntity: "system",
                                createdAt: entryCreatedAt,
                                entries: [
                                    {
                                        type: "date",
                                        time: entryCreatedAt,
                                    },
                                ],
                            });

                            dateGroup = {
                                createdByEntity: "system",
                                createdAt: entryCreatedAt,
                                entries: [
                                    {
                                        type: "date",
                                        time: entryCreatedAt,
                                    },
                                ],
                            };
                        }
                    }

                    if (index === entries.length - 1) {
                        _entryGroups.push(entryGroup);
                    }
                });
            }

            return _entryGroups;
        });

        const { websockets } = useWebsockets();
        const { openImageGalleryModal, showImageGalleryModal, hasImages } = useImageGalleryModal();
        return {
            chatLog,
            chatsStore,
            hasImages,
            openImageGalleryModal,
            resolveUserStatus,
            sentinel,
            sentinelIsVisible,
            showImageGalleryModal,
            websockets,
        };
    },
});
